import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { batchAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/BatchDetails.css';

const BatchDetails = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [batch, setBatch] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(false);

  useEffect(() => {
    fetchBatchDetails();
    if (isAuthenticated) {
      checkAccess();
    }
  }, [batchId, isAuthenticated]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      // console.log("Batch ID for getting details: ", batchId);
      const response = await batchAPI.getBatchDetails(batchId);
      if (response.data.success) {
        setBatch(response.data.batch);
        setIsEnrolled(response.data.isEnrolled);
      }
    } catch (error) {
      console.error('Error fetching batch details:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAccess = async () => {
    try {
      setCheckingAccess(true);
      const response = await batchAPI.checkBatchAccess(batchId);
      if (response.data.success) {
        setHasAccess(response.data.hasAccess);
      }
    } catch (error) {
      console.error('Error checking access:', error);
    } finally {
      setCheckingAccess(false);
    }
  };

  const handleEnroll = () => {
    if (!isAuthenticated) {
      alert('या बॅचमध्ये नोंदणी करण्यासाठी कृपया लॉगिन करा');
      navigate('/login');
      return;
    }
    if (batch.whatsappLink) {
      const message = `नमस्कार, मला ${batch.name} मध्ये नोंदणी करायची आहे. माझा ईमेल: ${user.email}`;
      const whatsappUrl = `${batch.whatsappLink}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  const handleAccessQuizzes = () => {
    navigate(`/premium-tests/${batchId}`);
  };

  const handleManageBatch = () => {
    navigate(`/manage-batch/${batchId}`);
  };

  const handleViewLeaderboard = () => {
    navigate(`/batch-leaderboard/${batchId}`);
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading-container">
          <div className="loader"></div>
          <p>बॅच तपशील लोड होत आहेत...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div>
        <Navbar />
        <div className="error-container">
          <div className="error-icon">😕</div>
          <h2>बॅच आढळला नाही</h2>
          <p>तुम्ही शोधत असलेला बॅच अस्तित्वात नाही किंवा काढून टाकला गेला आहे.</p>
          <button className="btn-back" onClick={() => navigate('/premium')}>
            ← बॅचेसकडे परत जा
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="batch-details-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="batch-hero">
        <div className="batch-hero-background">
          <img 
            src={batch.imageUrl || 'https://via.placeholder.com/1200x400'} 
            alt={batch.name}
            className="batch-hero-image"
          />
          <div className="batch-hero-gradient"></div>
        </div>
        <div className="batch-hero-content">
          <div className="container">
            <div className="batch-breadcrumb">
              <span onClick={() => navigate('/premium')} className="breadcrumb-link">
                प्रीमियम बॅचेस
              </span>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-current">{batch.name}</span>
            </div>
            <h1 className="batch-hero-title">{batch.name}</h1>
            <div className="batch-hero-meta">
              <span className="batch-meta-item">
                <span className="meta-icon">📚</span>
                {batch.subject}
              </span>
              <span className="batch-meta-item">
                <span className="meta-icon">👥</span>
                {batch.enrolledUsers?.length || 0} विद्यार्थी नोंदणीकृत
              </span>
              {batch.price > 0 && (
                <span className="batch-meta-item batch-price-tag">
                  <span className="meta-icon">💰</span>
                  ₹{batch.price}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="batch-content-wrapper">
        <div className="container">
          <div className="batch-content-grid">
            {/* Left Column - Info */}
            <div className="batch-info-column">
              {/* About Section */}
              <div className="content-card">
                <div className="card-header">
                  <h2>📖 या बॅचबद्दल</h2>
                </div>
                <div className="card-body">
                  <p className="batch-description-text">
                    {batch.description || 'तुमच्या तयारीत उत्कृष्टता मिळवण्यासाठी डिझाइन केलेला सर्वसमावेशक अभ्यासक्रम. हजारो यशस्वी विद्यार्थ्यांमध्ये सामील व्हा.'}
                  </p>
                </div>
              </div>

              {/* Features Section */}
              <div className="content-card">
                <div className="card-header">
                  <h2>✨ तुम्हाला काय मिळेल</h2>
                </div>
                <div className="card-body">
                  <div className="features-grid">
                    <div className="feature-item">
                      <div className="feature-icon">📝</div>
                      <div className="feature-content">
                        <h4>विशेष प्रश्नसंच</h4>
                        <p>या बॅचमधील सर्व प्रीमियम प्रश्नसंचांचा प्रवेश</p>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">📊</div>
                      <div className="feature-content">
                        <h4>कामगिरी ट्रॅकिंग</h4>
                        <p>तुमची प्रगती ट्रॅक करा आणि कमकुवत क्षेत्रे ओळखा</p>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">💡</div>
                      <div className="feature-content">
                        <h4>तपशीलवार उपाय</h4>
                        <p>प्रत्येक प्रश्नासाठी सर्वसमावेशक स्पष्टीकरण</p>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">⏰</div>
                      <div className="feature-content">
                        <h4>आजीवन प्रवेश</h4>
                        <p>तुमच्या गतीने, कधीही, कुठेही शिका</p>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">🔄</div>
                      <div className="feature-content">
                        <h4>नियमित अपडेट्स</h4>
                        <p>नियमितपणे नवीन प्रश्नसंच जोडले जातात</p>
                      </div>
                    </div>
                    <div className="feature-item">
                      <div className="feature-icon">🎯</div>
                      <div className="feature-content">
                        <h4>तज्ञांनी तयार केलेले</h4>
                        <p>विषय तज्ञांनी डिझाइन केलेली सामग्री</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="batch-stats-section">
                <div className="stat-card">
                  <div className="stat-icon">📚</div>
                  <div className="stat-value">50+</div>
                  <div className="stat-label">प्रश्नसंच</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">❓</div>
                  <div className="stat-value">1000+</div>
                  <div className="stat-label">प्रश्न</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-value">{batch.enrolledUsers?.length || 0}</div>
                  <div className="stat-label">विद्यार्थी</div>
                </div>
              </div>
            </div>

            {/* Right Column - Action Card */}
            <div className="batch-action-column">
              <div className="action-card-sticky">
                <div className="action-card">
                  {/* Price Display */}
                  {batch.price > 0 && (
                    <div className="price-section">
                      <div className="price-tag">
                        <span className="price-currency">₹</span>
                        <span className="price-value">{batch.price}</span>
                      </div>
                      <p className="price-note">एकवेळ पेमेंट • आजीवन प्रवेश</p>
                    </div>
                  )}

                  {/* Admin Actions */}
                  {user?.role === 'admin' && (
                    <div className="action-section">
                      <div className="admin-badge-large">
                        <span className="badge-icon">👑</span>
                        <span>प्रशासक प्रवेश</span>
                      </div>
                      <button onClick={handleManageBatch} className="btn-action btn-admin">
                        <span className="btn-icon">⚙️</span>
                        बॅच व्यवस्थापित करा
                      </button>
                      <button onClick={handleAccessQuizzes} className="btn-action btn-primary">
                        <span className="btn-icon">📝</span>
                        सर्व प्रश्नसंच पहा
                      </button>
                      <button onClick={handleViewLeaderboard} className="btn-action btn-leaderboard">
                        <span className="btn-icon">🏆</span>
                        लीडरबोर्ड पहा
                      </button>
                      <button 
                        onClick={() => navigate(`/update-batch/${batchId}`)} 
                        className="btn-action btn-admin"
                      >
                        <span className="btn-icon">✏️</span>
                        बॅच तपशील संपादित करा
                      </button>
                    </div>
                  )}

                  {/* User Actions - Enrolled */}
                  {user?.role !== 'admin' && (hasAccess || isEnrolled) && (
                    <div className="action-section">
                      <div className="enrolled-badge-large">
                        <span className="badge-icon">✅</span>
                        <div>
                          <div className="badge-title">तुमची नोंदणी झाली आहे!</div>
                          <div className="badge-subtitle">आता शिकणे सुरू करा</div>
                        </div>
                      </div>
                      <button onClick={handleAccessQuizzes} className="btn-action btn-success">
                        <span className="btn-icon">🚀</span>
                        प्रश्नसंच सुरू करा
                      </button>
                      <button onClick={handleViewLeaderboard} className="btn-action btn-leaderboard">
                        <span className="btn-icon">🏆</span>
                        लीडरबोर्ड पहा
                      </button>
                    </div>
                  )}

                  {/* User Actions - Not Enrolled */}
                  {user?.role !== 'admin' && !hasAccess && !isEnrolled && isAuthenticated && (
                    <div className="action-section">
                      <button onClick={handleEnroll} className="btn-action btn-whatsapp">
                        <span className="btn-icon">📱</span>
                        WhatsApp द्वारे नोंदणी करा
                      </button>
                      <p className="action-note">
                        <span className="note-icon">ℹ️</span>
                        त्वरित नोंदणीसाठी WhatsApp वर आमच्याशी संपर्क साधण्यासाठी क्लिक करा
                      </p>
                    </div>
                  )}

                  {/* Not Logged In */}
                  {!isAuthenticated && (
                    <div className="action-section">
                      <div className="login-prompt-card">
                        <div className="prompt-icon">🔐</div>
                        <h3>लॉगिन आवश्यक</h3>
                        <p>या बॅचमध्ये नोंदणी करण्यासाठी कृपया लॉगिन करा</p>
                      </div>
                      <button onClick={() => navigate('/login')} className="btn-action btn-google">
                        <span className="btn-icon">🔑</span>
                        Google सह लॉगिन करा
                      </button>
                    </div>
                  )}

                  {/* Trust Indicators */}
                  <div className="trust-section">
                    <div className="trust-item">
                      <span className="trust-icon">🔒</span>
                      <span>सुरक्षित पेमेंट</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-icon">💯</span>
                      <span>दर्जेदार सामग्री</span>
                    </div>
                    <div className="trust-item">
                      <span className="trust-icon">🎓</span>
                      <span>तज्ञ सहाय्य</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BatchDetails;